import React, { useEffect } from 'react'
import { withRouter } from 'react-router'
import TopicForm from './Form'
import HostingEdit from './HostingEdit'
import SectionsEdit from '../sections/EditMst'
import DocumentsEdit from '../documents/Edit'
import LmsCoursesEdit from '../lms_courses/Edit'
import TopicsNotificationsEdit from '../topic-notifications/Edit'
import ActionsEdit from '../actions/Edit'
import SocialMediaEdit from '../social-media/Form'
import SurveyQuestionsEdit from '../survey-questions/Edit'
import AppTabs from '../shared/AppTabs'
import { inject, observer } from 'mobx-react'
import { flow, startCase } from 'lodash'
import { IStore } from '../../store/models'
import { Typography } from '@material-ui/core'
import TopicConversations from './TopicConversations'
import TopicSubscribers from './TopicSubscribers'

const EditTopicPage = (props: FIXME) => {
  const {
    topic,
    location: { state: { from = '/admin' } = {} },
    match: {
      params: { tab }
    },
    history,
    submit,
    onTabChange,
    onDirty
  } = props

  // Each tab carries its own route slug so the conditional spreads below
  // (Hosting only for non-templates, Videos only when allowVideos) can be
  // added/removed without a parallel slug array drifting out of sync with the
  // rendered tab order. See inclusivv/civicdinners_client#695.
  const tabs = [
    {
      slug: '',
      label: 'General',
      content: (
        <>
          <div className='header-24 bold fat-bottom'>General Details</div>
          <TopicForm
            submit={submit}
            topic={topic}
            page='edit'
            onDirty={onDirty}
            onSave={(data) => {
              topic.setDirty(false)
              history.replace(`/admin/topics/${data.slug}/edit`, { from })
            }}
          />
        </>
      )
    },
    ...(topic && !topic.isTemplate
      ? [
          {
            slug: 'hosting',
            label: 'Hosting',
            content: (
              <>
                <div className='header-24 bold fat-bottom'>Hosting</div>
                <HostingEdit />
              </>
            )
          }
        ]
      : []),
    {
      slug: 'documents',
      label: 'Documents',
      content: (
        <>
          <div className='header-24 bold fat-bottom'>Documents</div>
          <DocumentsEdit documentable={topic} type='Topic' />
        </>
      )
    },
    ...(topic && topic?.allowVideos
      ? [
          {
            slug: 'lms_courses',
            label: 'Videos',
            content: (
              <>
                <div className='header-24 bold fat-bottom'>Videos</div>
                <LmsCoursesEdit topic={topic} />
              </>
            )
          }
        ]
      : []),
    {
      slug: 'emails',
      label: 'Emails',
      content: (
        <>
          <div className='header-24 bold fat-bottom'>Emails</div>
          <TopicsNotificationsEdit topic={topic} />
        </>
      )
    },
    {
      slug: 'social',
      label: 'Social & SEO',
      content: (
        <>
          <div className='header-24 bold fat-bottom'>Social Media & SEO</div>
          <SocialMediaEdit item={topic} onSave={topic?.updateTopic} />
        </>
      )
    },

    {
      slug: 'actions',
      label: 'Take Action',
      content: (
        <>
          <Typography variant='h5' className='fat-bottom'>
            Take Action
          </Typography>
          <ActionsEdit topic={topic} />
        </>
      )
    },

    {
      slug: 'questions',
      label: 'RSVP Questions',
      content: (
        <>
          <div className='header-24 bold fat-bottom'>RSVP Questions</div>
          <SurveyQuestionsEdit topic={topic} />
        </>
      )
    },
    {
      slug: 'sponsors',
      label: 'Sponsors',
      content: (
        <>
          <div className='header-24 bold fat-bottom'>Sponsors</div>
          <SectionsEdit sectionable={topic} type='Topic' />
        </>
      )
    },
    {
      slug: 'conversations',
      label: 'Conversations',
      wide: true,
      noBottom: true,
      content: (
        <div>
          <div className='header-24 bold fat-bottom fat-left'>Conversations</div>
          <TopicConversations topic={topic} />
        </div>
      )
    },
    {
      slug: 'subscribers',
      label: 'Subscribers',
      wide: true,
      noBottom: true,
      content: (
        <>
          <TopicSubscribers topic={topic} />
        </>
      )
    }
  ]

  let activeTab = tabs.findIndex((t) => t.slug === tab)
  activeTab = activeTab === -1 ? 0 : activeTab
  useEffect(() => {
    topic && onTabChange && onTabChange(activeTab)
    return () => {
      topic && onTabChange && onTabChange(0)
    }
  }, [topic, onTabChange, activeTab])

  if (!topic) return null

  return (
    <AppTabs
      index={activeTab}
      onChange={(tabIndex) => {
        history.push(`/admin/topics/${topic.slug}/edit/t/${tabs[tabIndex].slug}`, { from })
      }}
      tabs={tabs}
      contentStyle={{ height: 'calc(100vh - 17em)', overflowY: 'auto' }}
    />
  )
}

export default flow(
  observer,
  inject<FIXME, FIXME>(
    (
      {
        mst: { topics }
      }: {
        mst: IStore
      },
      {
        match: {
          params: { slug }
        }
      }
    ) => {
      const topic = topics.list.find((d) => d.slug === slug)
      !topic && topics.shouldLoad(slug, () => topics.loadBySlug(slug))
      topic && topic.shouldLoad('topicLmsCourses', topic.loadTopicLmsCourses)

      return {
        topic,
        topicLoading: topics.loadingIds.includes(slug)
      }
    }
  ),
  withRouter
)(EditTopicPage)
